import { memo, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Company, Location } from '../../stores/types';
import { getSiteTypeDisplayText } from '../../stores/types/sitetype.interface';
// Components
import { AddPlus2, ErrorWarningtraiangle, More } from '../../assets/icons';
import { USER_ROLE, useAuth } from '../../hooks';
import { useCompanyPermissions } from '../../hooks/useCompanyPermissions';
import { useNavigateWithSearchParam } from '../../hooks/useNavigateWithSearchParam';
import { useDeleteLocationMutation } from '../../services/location.api';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Grid,
  Label,
  LabelType,
  Menu,
  MenuItem,
  Skeleton,
} from '../_ui';
import { MODAL_TYPES, useGlobalModalContext } from '../_ui/modal/GlobalModal.component';
import { LocationEditor } from './LocationEditor.component';

interface Props {
  locations: Location[] | undefined;
  isLocationLoading?: boolean;
  company: Company | undefined;
}

const ROW_PER_PAGE = 10;

export const LocationInfo = memo(({ locations, isLocationLoading = false, company }: Props) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const { showModal } = useGlobalModalContext();
  const { canCreateLocations, canEditLocations } = useCompanyPermissions(company?.id || undefined);
  const { navigateWithSearchParams } = useNavigateWithSearchParam();
  const auth = useAuth();
  const isSupportUser = auth.role === USER_ROLE.SUPPORT;

  const handleAddLocationClick = () => {
    showModal(MODAL_TYPES.INFO_MODAL, {
      title: t('company_add_location'),
      width: '540px',
      height: 'max-content',
      onRenderBody: () => <LocationEditor company={company} />,
      shouldCloseOnOverlayClick: false,
    });
  };

  const renderHeader = () => {
    return (
      <div className='flex flex-row w-full justify-between items-center'>
        <div className='flex flex-row items-baseline gap-2'>
          <Label text={t('company_locations')} type={LabelType.H4} color={ColorType.BLACK} />
          {isLocationLoading && (
            <Skeleton height='h-5' width='w-60' innerClassName='items-center' />
          )}
          {!isLocationLoading && (
            <div className='text-sm font-normal font-inter text-grey5'>
              {(locations?.length || 0) < 10 ? (
                <Trans
                  i18nKey='company_number_locations'
                  values={{
                    number: locations?.length || 0,
                  }}
                />
              ) : (
                <Trans
                  i18nKey='company_number_locations_gt_10'
                  values={{
                    page: (currentPage - 1) * ROW_PER_PAGE + 1,
                    number:
                      currentPage * ROW_PER_PAGE > (locations?.length || 0)
                        ? locations?.length || 0
                        : ROW_PER_PAGE,
                    total: locations?.length || 0,
                  }}
                />
              )}
            </div>
          )}
        </div>

        {canCreateLocations && (
          <Button
            icon={AddPlus2}
            label={t('company_add_location')}
            onClick={handleAddLocationClick}
            type={ButtonType.TERTIARY}
            size={ButtonSize.SMALL}
            dataTestId='addNewLocation'
          />
        )}
      </div>
    );
  };

  const handleEditClick = (row: Location) => {
    showModal(MODAL_TYPES.INFO_MODAL, {
      title: t('location_edit'),
      width: '540px',
      height: 'max-content',
      onRenderBody: () => <LocationEditor defaultLocation={row} company={company} />,
      shouldCloseOnOverlayClick: false,
    });
  };

  const handleDeleteClick = (row: Location) => {
    showModal(MODAL_TYPES.ALERT_MODAL, {
      height: 'max-content',
      title: t('location_delete_warn_title'),
      icon: ErrorWarningtraiangle,
      iconColor: ColorType.NEGATIVE,
      message: (
        <Trans
          i18nKey='location_delete_warn_message'
          values={{ name: row.name }}
          components={{
            bold: <strong />,
          }}
        />
      ),
      mutationHook: useDeleteLocationMutation,
      mutationParams: {
        id: row.id,
        name: row.name,
      },
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
          className: 'min-w-fit w-20',
        },
        {
          label: t('delete'),
          type: ButtonType.DESTRUCTIVE,
          size: ButtonSize.SMALL,
          disableDelayTime: 3000,
          disableDelayLabel: t('wait_3_sec'),
          className: 'min-w-fit w-20',
          isSubmit: true,
          dataTestId: 'delete-location-button',
        },
      ],
      shouldCloseOnOverlayClick: false,
    });
  };

  const handleLocationClick = (location: Location) => {
    if (location.chargers && location.chargers.length > 0) {
      navigateWithSearchParams('/chargers', { locationId: location.id, companyId: company?.id });
    }
  };

  const getColumnTitle = () => {
    return [
      {
        key: 'name',
        title: t('company_location_name'),
      },
      {
        key: 'numberOfChargers',
        title: t('company_team_number_chargers'),
        component: (row: any) => (
          <Label
            text={`${row.chargers?.length || 0} ${t('company_locations_chargers')}`}
            type={LabelType.BODY3}
            color={row.chargers && row.chargers.length > 0 ? ColorType.BRAND2 : ColorType.BLACK}
            isLoading={isLocationLoading}
            skeletonHeight='h-5'
            onClick={
              row.chargers && row.chargers.length > 0 ? () => handleLocationClick(row) : undefined
            }
            className='whitespace-nowrap'
          />
        ),
      },
      {
        key: 'address',
        title: t('company_location_address'),
        component: (row: any) => (
          <Label
            className='whitespace-nowrap'
            text={`${row.streetAddress}, ${row.city}, ${row.stateOrRegion}, ${row.zipOrPostalCode}`}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
            isLoading={isLocationLoading}
            skeletonHeight='h-5'
          />
        ),
      },
      {
        key: 'siteType',
        title: t('site_type.label'),
        component: (row: Location) => (
          <Label
            className='whitespace-nowrap'
            text={getSiteTypeDisplayText(row.siteType, t)}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
            isLoading={isLocationLoading}
            skeletonHeight='h-5'
          />
        ),
      },
      {
        key: 'button',
        title: t(''),
        component: (row: Location) =>
          canEditLocations || isSupportUser ? (
            <div className='flex flex-row gap-[16px] justify-end pr-2'>
              <Menu
                placement='bottom-end'
                icon={More}
                iconAlt={t('icon_alt_more_options')}
                dataTestId={`locationMenuBar-${row.name}`}
              >
                {canEditLocations && (
                  <MenuItem
                    label={t('location_edit')}
                    onClick={() => handleEditClick(row)}
                    dataTestId='locationItem'
                  />
                )}
                {isSupportUser && (
                  <MenuItem
                    label={t('delete')}
                    labelColor={ColorType.NEGATIVE}
                    onClick={() => handleDeleteClick(row)}
                    disabled={(row.chargers?.length || 0) > 0}
                    dataTestId='locationDeleteItem'
                  />
                )}
              </Menu>
            </div>
          ) : null,
      },
    ];
  };

  const data = useMemo(() => {
    if (locations && locations.length > 0) {
      const startIndex = (currentPage - 1) * ROW_PER_PAGE;
      return locations.slice(startIndex, startIndex + ROW_PER_PAGE);
    }
    return [];
  }, [currentPage, locations]);

  const renderLocationGrid = () => {
    return (
      <Grid
        pageIndex={currentPage}
        loadPage={(page: number) => setCurrentPage(page)}
        columns={getColumnTitle()}
        data={data}
        primaryKey='id'
        totalPage={Math.ceil((locations?.length || 0) / ROW_PER_PAGE)}
        rowCount={ROW_PER_PAGE}
        isLoading={isLocationLoading}
      />
    );
  };

  return (
    <div className='flex flex-col'>
      {renderHeader()}
      <div className='mt-[24px]'>{renderLocationGrid()}</div>
    </div>
  );
});

LocationInfo.displayName = 'LocationInfo';
